import axios from 'axios';
import AsyncStorage from '../AsyncStorageShim';
import ListsManager from './ListsManager';
import API_BASE_URL from '../config';


class AdminManager {
    static baseUrl = API_BASE_URL;

    /**
     * Set the base URL for API requests
     * @param {string} url - The base URL
     */
    static setBaseUrl(url) {
        if (url) {
            this.baseUrl = url.replace(/\/$/, "");
            console.log(`📝 AdminManager: Base URL updated to ${this.baseUrl}`);
        }
    }
    /**
     * Get authentication headers with Firebase token
     */
    static async getAuthHeaders() {
        // Use ListsManager to get a robust token (waits for auth init if needed)
        const token = await ListsManager.getAuthToken();
        const tokenStatus = token ? 'Token exists' : 'No token found';

        if (!token) {
            console.warn('⚠️ AdminManager: No valid token obtained!');
        }

        return {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        };
    }

    /**
     * Check if current user is admin
     */
    static async checkAdminStatus() {
        console.log('🔍 AdminManager: Checking admin status...');
        try {
            const headers = await this.getAuthHeaders();
            console.log('📡 AdminManager: Making request to:', `${AdminManager.baseUrl}/listeners/is_admin`);
            const response = await axios.get(`${AdminManager.baseUrl}/listeners/is_admin`, { headers });
            console.log('✅ AdminManager: Response received:', response.data);
            return response.data.is_admin || false;
        } catch (error) {
            console.error('❌ AdminManager: Error checking admin status:', error);
            console.error('❌ AdminManager: Error details:', JSON.stringify(error.response?.data || error.message, null, 2));
            return false;
        }
    }

    /**
     * Register a new listener (user)
     */
    static async registerListener(listenerData) {
        try {
            const headers = await this.getAuthHeaders();
            const response = await axios.post(
                `${AdminManager.baseUrl}/listeners/add`,
                {
                    id: listenerData.id,
                    username: listenerData.username,
                    email: listenerData.email,
                    general_request: listenerData.general_request || null
                },
                { headers }
            );
            return response.data;
        } catch (error) {
            console.error('Error registering listener:', error);
            throw error;
        }
    }

    /**
     * Register a new Firebase user and create listener
     * @param {Object} userData - { name, email, password }
     * @returns {Promise<Object>} - { success: boolean, userId?: string, error?: string }
     */
    static async registerFirebaseUser(userData) {
        try {
            console.log('🔥 AdminManager: Registering Firebase user...');

            // Import Firebase dynamically
            const { getAuth, createUserWithEmailAndPassword, updateProfile } = await import('firebase/auth');
            const auth = getAuth();

            // Create user in Firebase
            const userCredential = await createUserWithEmailAndPassword(
                auth,
                userData.email,
                userData.password
            );

            const userId = userCredential.user.uid;
            console.log('✅ AdminManager: Firebase user created with ID:', userId);

            // Set display name in Firebase
            if (userData.name) {
                await updateProfile(userCredential.user, { displayName: userData.name });
                console.log('✅ AdminManager: Display name set to:', userData.name);
            }

            // Now create the listener in your database
            const listenerData = {
                id: userId,
                username: userData.email,
                email: userData.email
            };

            await this.registerListener(listenerData);
            console.log('✅ AdminManager: Listener created successfully');

            return {
                success: true,
                userId: userId
            };
        } catch (error) {
            console.error('❌ AdminManager: Error registering Firebase user:', error);

            // Parse Firebase error messages
            let errorMessage = 'Failed to register user';
            if (error.code === 'auth/email-already-in-use') {
                errorMessage = 'Email address is already in use';
            } else if (error.code === 'auth/invalid-email') {
                errorMessage = 'Invalid email address';
            } else if (error.code === 'auth/weak-password') {
                errorMessage = 'Password is too weak';
            } else if (error.message) {
                errorMessage = error.message;
            }

            return {
                success: false,
                error: errorMessage
            };
        }
    }

    /**
     * Generate a suggested list (Admin only)
     */
    static async generateSuggestedList(listData) {
        try {
            const headers = await this.getAuthHeaders();
            const response = await axios.post(
                `${AdminManager.baseUrl}/suggested_lists/generate`,
                {
                    list_name: listData.list_name,
                    list_prompt: listData.list_prompt || null,
                    usernames: listData.usernames
                },
                { headers }
            );
            return response.data;
        } catch (error) {
            console.error('Error generating suggested list:', error);
            throw error;
        }
    }

    /**
     * Get all suggested lists
     */
    static async getSuggestedLists(limit = 50, offset = 0) {
        try {
            const headers = await this.getAuthHeaders();
            const response = await axios.get(
                `${AdminManager.baseUrl}/get_suggested_lists_for_listener`,
                {
                    headers,
                    params: { limit, offset }
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error fetching suggested lists:', error);
            throw error;
        }
    }

    /**
     * Get ALL suggested lists (not just user-specific)
     * Uses admin endpoint to get all lists without user filtering
     */
    static async getAllSuggestedLists() {
        try {
            const headers = await this.getAuthHeaders();
            console.log('📋 AdminManager: Fetching all suggested lists from admin endpoint...');
            const response = await axios.get(
                `${AdminManager.baseUrl}/admin/suggested-lists`,
                { headers }
            );
            console.log('✅ AdminManager: Fetched', response.data.length, 'suggested lists');

            // Log the first few list IDs for debugging
            if (response.data.length > 0) {
                console.log('📋 First 3 list IDs received:');
                response.data.slice(0, 3).forEach((list, idx) => {
                    console.log(`  ${idx + 1}. ID: ${list.id}, Name: ${list.list_name}`);
                });
            }

            return response.data;
        } catch (error) {
            console.error('❌ AdminManager: Error fetching all suggested lists:', error);
            throw error;
        }
    }



    /**
     * Get comprehensive stats for the dashboard
     */
    static async getComprehensiveStats() {
        try {
            const headers = await this.getAuthHeaders();
            console.log('📊 AdminManager: Fetching comprehensive stats...');
            const response = await axios.get(
                `${AdminManager.baseUrl}/admin/comprehensive_stats`,
                { headers }
            );
            console.log('✅ AdminManager: Fetched comprehensive stats');
            return response.data;
        } catch (error) {
            console.error('❌ AdminManager: Error fetching detailed stats:', error);
            throw error;
        }
    }

    /**
     * Get aggregated listeners data (with counts)
     */
    static async getListenersData() {
        try {
            const headers = await this.getAuthHeaders();
            console.log('👥 AdminManager: Fetching listeners data...');
            const response = await axios.get(
                `${AdminManager.baseUrl}/admin/listeners_data`,
                { headers }
            );
            console.log('✅ AdminManager: Fetched', response.data.length, 'listeners data');
            return response.data;
        } catch (error) {
            console.error('❌ AdminManager: Error fetching listeners data:', error);
            console.error('❌ AdminManager: Error details:', JSON.stringify(error.response?.data || error.message, null, 2));
            throw error;
        }
    }

    /**
     * Get all listeners (users)
     */
    static async getAllListeners(searchEmail = '') {
        try {
            const headers = await this.getAuthHeaders();
            console.log('👥 AdminManager: Fetching listeners...', searchEmail ? `(search: ${searchEmail})` : '');
            const response = await axios.get(
                `${AdminManager.baseUrl}/admin/listeners`,
                {
                    headers,
                    params: { search: searchEmail }
                }
            );
            console.log('✅ AdminManager: Fetched', response.data.length, 'listeners');
            return response.data;
        } catch (error) {
            console.error('❌ AdminManager: Error fetching listeners:', error);
            console.error('❌ AdminManager: Error details:', error.response?.data || error.message);
            throw error;
        }
    }

    /**
     * Assign suggested lists to users
     * @param {Array} listIds - Array of suggested list IDs
     * @param {Array} listenerIds - Array of listener IDs
     */
    static async assignSuggestedListsToUsers(listIds, listenerIds) {
        try {
            console.log('📋 AdminManager: Assigning lists to users...', { listIds, listenerIds });
            const headers = await this.getAuthHeaders();

            const assignments = [];
            for (const listenerId of listenerIds) {
                for (const listId of listIds) {
                    try {
                        const response = await axios.post(
                            `${AdminManager.baseUrl}/admin/assign_suggested_lists/${listenerId}/${listId}`,
                            {},
                            { headers }
                        );
                        assignments.push({ listenerId, listId, success: true });
                    } catch (error) {
                        console.error(`Failed to assign list ${listId} to user ${listenerId}:`, error);
                        assignments.push({ listenerId, listId, success: false, error: error.message });
                    }
                }
            }

            const successCount = assignments.filter(a => a.success).length;
            console.log(`✅ AdminManager: Assigned ${successCount}/${assignments.length} list-user combinations`);

            return {
                success: successCount > 0,
                total: assignments.length,
                successful: successCount,
                failed: assignments.length - successCount,
                details: assignments
            };
        } catch (error) {
            console.error('❌ AdminManager: Error assigning lists:', error);
            throw error;
        }
    }

    /**
     * Get suggested list details
     */
    static async getSuggestedListDetails(listId) {
        try {
            const headers = await this.getAuthHeaders();
            const response = await axios.get(
                `${AdminManager.baseUrl}/get_suggested_lists_details/${listId}`,
                { headers }
            );
            return response.data;
        } catch (error) {
            console.error('Error fetching suggested list details:', error);
            throw error;
        }
    }

    /**
     * Get database statistics (if endpoint exists)
     */
    static async getDatabaseStats() {
        try {
            const headers = await this.getAuthHeaders();
            const response = await axios.get(`${AdminManager.baseUrl}/admin/db-stats`, { headers });
            return response.data;
        } catch (error) {
            console.error('Error fetching database stats:', error);
            return null;
        }
    }

    /**
     * Get message statistics for a listener
     */
    static async getMessageStats(windowMinutes = 60) {
        try {
            const headers = await this.getAuthHeaders();
            const response = await axios.get(
                `${AdminManager.baseUrl}/stats/messages`,
                {
                    headers,
                    params: { window_minutes: windowMinutes }
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error fetching message stats:', error);
            return null;
        }
    }

    /**
     * Assign suggested lists to a user
     */
    static async assignSuggestedLists(listenerId, userListName) {
        try {
            const headers = await this.getAuthHeaders();
            const response = await axios.post(
                `${AdminManager.baseUrl}/assign_suggested_lists/${listenerId}/${userListName}`,
                {},
                { headers }
            );
            return response.data;
        } catch (error) {
            console.error('Error assigning suggested lists:', error);
            throw error;
        }
    }

    /**
     * Get rich listener analytics (engagement, feature usage, trends)
     */
    static async getListenerAnalytics(days = 30) {
        try {
            const headers = await this.getAuthHeaders();
            console.log('📊 AdminManager: Fetching listener analytics...');
            const response = await axios.get(
                `${AdminManager.baseUrl}/admin/analytics`,
                {
                    headers,
                    params: { days }
                }
            );
            console.log('✅ AdminManager: Fetched analytics data');
            return response.data;
        } catch (error) {
            console.error('❌ AdminManager: Error fetching analytics:', error);
            throw error;
        }
    }

    /**
     * Get deep analytics for a single listener
     */
    static async getSingleListenerAnalytics(listenerId, days = 90) {
        try {
            const headers = await this.getAuthHeaders();
            const response = await axios.get(
                `${AdminManager.baseUrl}/admin/listeners/${listenerId}/analytics`,
                { headers, params: { days } }
            );
            return response.data;
        } catch (error) {
            console.error('❌ AdminManager: Error fetching single listener analytics:', error);
            throw error;
        }
    }

    /**
     * Generate demo data for a client pitch
     */
    static async generateDemo(orgName, orgDescription, language = 'he') {
        try {
            const headers = await this.getAuthHeaders();
            const response = await axios.post(
                `${AdminManager.baseUrl}/admin/generate-demo`,
                { org_name: orgName, org_description: orgDescription, language },
                { headers, timeout: 30000 }
            );
            return response.data;
        } catch (error) {
            console.error('❌ AdminManager: Error generating demo:', error);
            throw error;
        }
    }

    /**
     * Update a listener's display name (Firebase)
     */
    static async updateListenerProfile(listenerId, { displayName }) {
        try {
            const headers = await this.getAuthHeaders();
            const response = await axios.patch(
                `${AdminManager.baseUrl}/admin/listeners/${listenerId}/profile`,
                { display_name: displayName },
                { headers }
            );
            return response.data;
        } catch (error) {
            console.error('Error updating listener profile:', error);
            throw error;
        }
    }

    /**
     * Send password reset link for a listener
     */
    static async sendPasswordReset(listenerId) {
        try {
            const headers = await this.getAuthHeaders();
            const response = await axios.post(
                `${AdminManager.baseUrl}/admin/listeners/${listenerId}/reset-password`,
                {},
                { headers }
            );
            return response.data;
        } catch (error) {
            console.error('Error sending password reset:', error);
            throw error;
        }
    }

    /**
     * Get listener activity timeline
     */
    static async getListenerActivity(listenerId, limit = 50) {
        try {
            const headers = await this.getAuthHeaders();
            const response = await axios.get(
                `${AdminManager.baseUrl}/admin/listeners/${listenerId}/activity`,
                {
                    headers,
                    params: { limit }
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error fetching listener activity:', error);
            throw error;
        }
    }

    // =========================================================================
    // PRODUCT DETECTION
    // =========================================================================

    static async getProducts(orgId = null) {
        const headers = await this.getAuthHeaders();
        const params = orgId ? { org_id: orgId } : {};
        const response = await axios.get(`${AdminManager.baseUrl}/admin/products`, { headers, params });
        return response.data;
    }

    static async getProduct(productId) {
        const headers = await this.getAuthHeaders();
        const response = await axios.get(`${AdminManager.baseUrl}/admin/products/${productId}`, { headers });
        return response.data;
    }

    static async createProduct({ orgId, listId, brandName, productName, description, searchTerms }) {
        const headers = await this.getAuthHeaders();
        const response = await axios.post(`${AdminManager.baseUrl}/admin/products`, {
            org_id: orgId,
            list_id: listId,
            brand_name: brandName,
            product_name: productName,
            description: description || null,
            search_terms: searchTerms,
        }, { headers });
        return response.data;
    }

    static async updateProduct(productId, updates) {
        const headers = await this.getAuthHeaders();
        const response = await axios.patch(`${AdminManager.baseUrl}/admin/products/${productId}`, updates, { headers });
        return response.data;
    }

    static async deleteProduct(productId) {
        const headers = await this.getAuthHeaders();
        const response = await axios.delete(`${AdminManager.baseUrl}/admin/products/${productId}`, { headers });
        return response.data;
    }

    static async scrapeProductImages(productId) {
        const headers = await this.getAuthHeaders();
        const response = await axios.post(
            `${AdminManager.baseUrl}/admin/products/${productId}/scrape-images`,
            {},
            { headers, timeout: 120000 }
        );
        return response.data;
    }

    static async scrapeSocialImages(productId, platforms = null) {
        const headers = await this.getAuthHeaders();
        let url = `${AdminManager.baseUrl}/admin/products/${productId}/scrape-social-images`;
        if (platforms && platforms.length > 0) {
            const params = platforms.map(p => `platforms=${p}`).join('&');
            url += `?${params}`;
        }
        const response = await axios.post(url, {}, { headers, timeout: 300000 });
        return response.data;
    }

    static async rebuildAndTrain() {
        const headers = await this.getAuthHeaders();
        const response = await axios.post(
            `${AdminManager.baseUrl}/admin/products/rebuild-and-train`,
            {},
            { headers, timeout: 30000 }
        );
        return response.data;
    }

    static async getRecentDetections(orgId = null, limit = 50) {
        const headers = await this.getAuthHeaders();
        const params = { limit };
        if (orgId) params.org_id = orgId;
        const response = await axios.get(`${AdminManager.baseUrl}/admin/products/detections/recent`, { headers, params });
        return response.data;
    }

    static async getProductDetections(productId, limit = 50) {
        const headers = await this.getAuthHeaders();
        const response = await axios.get(
            `${AdminManager.baseUrl}/admin/products/${productId}/detections`,
            { headers, params: { limit } }
        );
        return response.data;
    }

    static async getProductImages(productId) {
        const headers = await this.getAuthHeaders();
        const response = await axios.get(
            `${AdminManager.baseUrl}/admin/products/${productId}/images`,
            { headers }
        );
        return response.data;
    }

    static async tagProductImages(productId, tags) {
        const headers = await this.getAuthHeaders();
        const response = await axios.post(
            `${AdminManager.baseUrl}/admin/products/${productId}/images/tag`,
            { tags },
            { headers }
        );
        return response.data;
    }

    static async deleteRejectedImages(productId) {
        const headers = await this.getAuthHeaders();
        const response = await axios.delete(
            `${AdminManager.baseUrl}/admin/products/${productId}/images/rejected`,
            { headers }
        );
        return response.data;
    }

    static async getTrainingStatus(jobId) {
        const headers = await this.getAuthHeaders();
        const response = await axios.get(
            `${AdminManager.baseUrl}/admin/products/training-status/${jobId}`,
            { headers }
        );
        return response.data;
    }

    static async getTrainingJobs(limit = 20) {
        const headers = await this.getAuthHeaders();
        const response = await axios.get(
            `${AdminManager.baseUrl}/admin/products/training-jobs`,
            { headers, params: { limit } }
        );
        return response.data;
    }

    static async scrapeNegativeImages() {
        const headers = await this.getAuthHeaders();
        const response = await axios.post(
            `${AdminManager.baseUrl}/admin/products/scrape-negatives`,
            {},
            { headers, timeout: 180000 }
        );
        return response.data;
    }

    static async autoAnnotateProduct(productId) {
        const headers = await this.getAuthHeaders();
        const response = await axios.post(
            `${AdminManager.baseUrl}/admin/products/${productId}/auto-annotate`,
            {},
            { headers, timeout: 300000 }
        );
        return response.data;
    }

    static async getProductBBoxes(productId) {
        const headers = await this.getAuthHeaders();
        const response = await axios.get(
            `${AdminManager.baseUrl}/admin/products/${productId}/bboxes`,
            { headers }
        );
        return response.data;
    }

    // =========================================================================
    // ARTICLE ANALYZER
    // =========================================================================

    static async getOrgLists(orgId) {
        const headers = await this.getAuthHeaders();
        const response = await axios.get(
            `${AdminManager.baseUrl}/admin/orgs/${orgId}/lists`,
            { headers }
        );
        return response.data;
    }

    static async analyzeArticle(url, listId) {
        console.log('[analyzeArticle] called with:', { url, listId });
        try {
            const headers = await this.getAuthHeaders();
            console.log('[analyzeArticle] sending request to:', `${AdminManager.baseUrl}/admin/analyze-article`);
            const response = await axios.post(
                `${AdminManager.baseUrl}/admin/analyze-article`,
                { url, list_id: listId },
                { headers, timeout: 60000 }
            );
            console.log('[analyzeArticle] response:', response.status, response.data);
            return response.data;
        } catch (e) {
            console.error('[analyzeArticle] error:', e.message, e.response?.status, e.response?.data);
            throw e;
        }
    }

    static async saveProductBBoxes(productId, bboxes) {
        const headers = await this.getAuthHeaders();
        const response = await axios.post(
            `${AdminManager.baseUrl}/admin/products/${productId}/bboxes`,
            { bboxes },
            { headers }
        );
        return response.data;
    }
}


export default AdminManager;
